import { Aircraft } from '../types/intellijetnet'
import { AircraftOverride } from '../types/database'

/**
 * Type for override field names that can be overridden
 */
export type OverridableField =
  | 'aftt'
  | 'landings'
  | 'baseairport'
  | 'basecity'
  | 'basestate'
  | 'basecountry'
  | 'forsale'
  | 'leased'
  | 'asking'
  | 'status'
  | 'mxprog'
  | 'damagehistoryyn'
  | 'datelisted'
  | 'datepurchased'
  | 'yearmfr'
  | 'yeardlv'
  | 'custom_1_value'
  | 'custom_2_value'
  | 'custom_3_value'

/**
 * Mapping between aircraft fields and override fields
 */
const FIELD_MAPPING: Record<OverridableField, keyof AircraftOverride> = {
  aftt: 'aftt',
  landings: 'landings',
  baseairport: 'base_airport',
  basecity: 'base_city',
  basestate: 'base_state',
  basecountry: 'base_country',
  forsale: 'for_sale',
  leased: 'leased',
  asking: 'asking_price',
  status: 'status',
  mxprog: 'maintenance_program',
  damagehistoryyn: 'damage_history',
  datelisted: 'date_listed',
  datepurchased: 'date_purchased',
  yearmfr: 'year_manufactured',
  yeardlv: 'year_delivered',
  custom_1_value: 'custom_1_value',
  custom_2_value: 'custom_2_value',
  custom_3_value: 'custom_3_value'
}

/**
 * Get the effective value for a specific aircraft field, considering overrides
 * @param fieldName - The field name from the Aircraft interface
 * @param aircraft - The aircraft data from the API
 * @param override - The override data (if any)
 * @returns The effective value (override if exists, otherwise API value)
 */
export function getEffectiveAircraftField<T extends OverridableField>(
  fieldName: T,
  aircraft: Aircraft,
  override?: AircraftOverride | null
): any {
  // Handle custom columns separately since they don't exist on Aircraft interface
  if (fieldName.startsWith('custom_')) {
    if (!override) return null
    const overrideFieldName = FIELD_MAPPING[fieldName]
    return override[overrideFieldName] || null
  }

  if (!override) {
    return aircraft[fieldName as keyof Aircraft]
  }

  const overrideFieldName = FIELD_MAPPING[fieldName]
  const overrideValue = override[overrideFieldName]

  // Return override value if it exists (including false/0), otherwise return API value
  return overrideValue !== null && overrideValue !== undefined
    ? overrideValue
    : aircraft[fieldName as keyof Aircraft]
}

/**
 * Get all effective aircraft data with overrides applied
 * @param aircraft - The aircraft data from the API
 * @param override - The override data (if any)
 * @returns Aircraft data with overrides applied
 */
export function getEffectiveAircraftData(
  aircraft: Aircraft,
  override?: AircraftOverride | null
): Aircraft & { take_price?: number | null; emp?: string | null; tbo?: number | null; pax?: number | null; paint_date?: number | null; interior_date?: number | null } {
  try {
    if (!aircraft) {
      // Return a minimal aircraft object with all expected properties
      return {
        aircraft_id: 0,
        model_id: 0,
        sernbr: null,
        regnbr: null,
        yearmfr: null,
        yeardlv: null,
        aftt: null,
        landings: null,
        baseairport: null,
        basecity: null,
        basestate: null,
        basecountry: null,
        forsale: null,
        leased: null,
        asking: null,
        status: null,
        mxprog: null,
        damagehistoryyn: null,
        datelisted: null,
        datepurchased: null,
        take_price: null,
        emp: null,
        tbo: null,
        pax: null,
        paint_date: null,
        interior_date: null
      } as any
    }

    if (!override) {
      return { ...aircraft, take_price: null, emp: null, tbo: null, pax: null, paint_date: null, interior_date: null }
    }

    return {
      ...aircraft,
      aftt: getEffectiveAircraftField('aftt', aircraft, override),
      landings: getEffectiveAircraftField('landings', aircraft, override),
      baseairport: getEffectiveAircraftField('baseairport', aircraft, override),
      basecity: getEffectiveAircraftField('basecity', aircraft, override),
      basestate: getEffectiveAircraftField('basestate', aircraft, override),
      basecountry: getEffectiveAircraftField('basecountry', aircraft, override),
      forsale: getEffectiveAircraftField('forsale', aircraft, override),
      leased: getEffectiveAircraftField('leased', aircraft, override),
      asking: getEffectiveAircraftField('asking', aircraft, override),
      status: getEffectiveAircraftField('status', aircraft, override),
      mxprog: getEffectiveAircraftField('mxprog', aircraft, override),
      damagehistoryyn: getEffectiveAircraftField('damagehistoryyn', aircraft, override),
      datelisted: getEffectiveAircraftField('datelisted', aircraft, override),
      datepurchased: getEffectiveAircraftField('datepurchased', aircraft, override),
      yearmfr: getEffectiveAircraftField('yearmfr', aircraft, override),
      yeardlv: getEffectiveAircraftField('yeardlv', aircraft, override),
      take_price: override.take_price,
      emp: override.emp,
      tbo: override.tbo,
      pax: override.pax,
      paint_date: override.paint_date,
      interior_date: override.interior_date
    }
  } catch (error) {
    // Return original aircraft data as fallback
    return { ...aircraft, take_price: null, emp: null, tbo: null, pax: null, paint_date: null, interior_date: null }
  }
}

/**
 * Check if a field has an override value
 * @param fieldName - The field name from the Aircraft interface
 * @param override - The override data
 * @returns True if the field has an override value
 */
export function hasOverride(
  fieldName: OverridableField,
  override?: AircraftOverride | null
): boolean {
  if (!override) return false

  const overrideFieldName = FIELD_MAPPING[fieldName]
  const overrideValue = override[overrideFieldName]

  return overrideValue !== null && overrideValue !== undefined
}

/**
 * Convert API damage history codes to database format
 * @param apiValue - The damage history value from the API ('Y', 'N', 'U', etc.)
 * @returns Database-compatible value ('Yes', 'No', 'Unknown', or null)
 */
function convertDamageHistory(apiValue: string | null): 'Yes' | 'No' | 'Unknown' | null {
  if (!apiValue) return null

  switch (apiValue.toUpperCase()) {
    case 'Y':
    case 'YES':
      return 'Yes'
    case 'N':
    case 'NO':
      return 'No'
    case 'U':
    case 'UNKNOWN':
      return 'Unknown'
    default:
      return null
  }
}

/**
 * Convert aircraft data to override input format
 * @param aircraft - The aircraft data
 * @returns Override input data
 */
export function aircraftToOverrideInput(aircraft: Aircraft): Partial<AircraftOverride> {
  return {
    aircraft_id: aircraft.aircraft_id,
    aftt: aircraft.aftt,
    landings: aircraft.landings,
    base_airport: aircraft.baseairport,
    base_city: aircraft.basecity,
    base_state: aircraft.basestate,
    base_country: aircraft.basecountry,
    for_sale: aircraft.forsale,
    leased: aircraft.leased,
    asking_price: aircraft.asking ? parseFloat(aircraft.asking) : null,
    status: aircraft.status,
    maintenance_program: aircraft.mxprog,
    damage_history: convertDamageHistory(aircraft.damagehistoryyn),
    date_listed: aircraft.datelisted,
    date_purchased: aircraft.datepurchased
  }
}

/**
 * Get effective custom column value for an aircraft
 * @param columnIndex - The custom column index (1, 2, or 3)
 * @param aircraft - The aircraft data from API
 * @param override - The override data
 * @returns The effective custom column value (override takes precedence)
 */
export function getEffectiveCustomColumnValue(
  columnIndex: 1 | 2 | 3,
  _aircraft: Aircraft,
  override?: AircraftOverride | null
): string | null {
  if (!override) return null

  const fieldName = `custom_${columnIndex}_value` as keyof AircraftOverride
  const overrideValue = override[fieldName] as string | null

  // Return override value if it exists, otherwise null (no API fallback for custom columns)
  return overrideValue || null
}

/**
 * Check if a custom column has an override value
 * @param columnIndex - The custom column index (1, 2, or 3)
 * @param override - The override data
 * @returns True if the custom column has an override value
 */
export function hasCustomColumnOverride(
  columnIndex: 1 | 2 | 3,
  override?: AircraftOverride | null
): boolean {
  if (!override) return false

  const fieldName = `custom_${columnIndex}_value` as keyof AircraftOverride
  const overrideValue = override[fieldName] as string | null

  return overrideValue !== null && overrideValue !== undefined && overrideValue.trim() !== ''
}

/**
 * Set custom column value in override data
 * @param override - The override data
 * @param columnIndex - The custom column index (1, 2, or 3)
 * @param value - The value to set
 * @returns Updated override data
 */
export function setCustomColumnValue(
  override: Partial<AircraftOverride>,
  columnIndex: 1 | 2 | 3,
  value: string | null
): Partial<AircraftOverride> {
  const fieldName = `custom_${columnIndex}_value` as keyof AircraftOverride

  return {
    ...override,
    [fieldName]: value
  }
}
